import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import html2pdf from "html2pdf.js";

const CommercialInvoiceForm = ({
  isOpen,
  onClose,
  selectedOrder,
  onConfirmInvoice,
  creatingInvoice = false,
  companyInfo = {
    name: "Axia B2B Solutions",
    address: "123 Business Park, Suite 100",
    city: "Tech City",
    state: "State",
    zipCode: "12345",
    country: "India",
    phone: "+91 98765 43210",
    email: "sales@ennovaceb2b.com",
    website: "www.ennovaceb2b.com",
    gstin: "27ABCDE1234F1Z5",
    pan: "ABCDE1234F",
    bankDetails: {
      bankName: "HDFC Bank",
      accountName: "Axia B2B Solutions",
      accountNumber: "1234567890123456",
      ifscCode: "HDFC0001234",
      branch: "Tech City Branch",
      accountType: "Current Account",
    },
  },
}) => {
  const [selectedDeliveryOrders, setSelectedDeliveryOrders] = useState([]);
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: "",
    invoiceDate: format(new Date(), "yyyy-MM-dd"),
    dueDate: format(
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      "yyyy-MM-dd"
    ),
    poNumber: "",
    poDate: "",
    deliveryOrderNumber: "",
    deliveryOrderDate: "",
    paymentTerms: "Net 30 Days",
    placeOfSupply: "",
    transportMode: "Road",
    vehicleNumber: "",
    dateOfSupply: format(new Date(), "yyyy-MM-dd"),
    reverseCharge: "No",
    notes: "",
    termsAndConditions: `1. Payment due within 30 days of invoice date.
2. Interest @24% p.a. will be charged on overdue amounts.
3. All disputes subject to [City] jurisdiction only.
4. Goods once sold will not be taken back.
5. Delivery challan to be returned duly signed.
6. All prices are excluding applicable taxes unless specified.`,
    items: [],
    subtotal: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    cessAmount: 0,
    tcsAmount: 0,
    roundOff: 0,
    totalAmount: 0,
    amountInWords: "",
  });

  // Initialize invoice data when order is selected
  useEffect(() => {
    if (selectedOrder && isOpen) {
      // Initialize with all uninvoiced delivery orders selected
      const uninvoicedDOs = (selectedOrder.deliveryOrders || []).filter(
        (do_) => !do_.invoiced && do_.deliveryStatus === "delivered"
      );
      setSelectedDeliveryOrders(uninvoicedDOs.map((do_) => do_.id));
    }
  }, [selectedOrder, isOpen]);

  // Recalculate invoice data when selected delivery orders change
  useEffect(() => {
    if (selectedOrder && isOpen && selectedDeliveryOrders.length > 0) {
      // Use standardized invoice numbering format: INV-CUSTID-YEAR-SEQUENCE
      const customerId = selectedOrder.customer?.id || "000";
      const currentYear = new Date().getFullYear();

      // Get selected delivery orders
      const deliveryOrders = (selectedOrder.deliveryOrders || []).filter(
        (do_) => selectedDeliveryOrders.includes(do_.id)
      );

      // Combine items from all selected delivery orders
      const allDeliveryItems = [];
      deliveryOrders.forEach((do_) => {
        if (do_.items) {
          allDeliveryItems.push(...do_.items);
        }
      });

      // Use delivery order items if available, otherwise fall back to order items
      const sourceItems =
        allDeliveryItems.length > 0
          ? allDeliveryItems
          : selectedOrder.orderItems || [];

      // Calculate items with proper tax breakdown
      const items = sourceItems.map((item, index) => {
        // For delivery items, get the corresponding order item for pricing and product details
        const orderItem =
          item.orderItem ||
          selectedOrder.orderItems?.find(
            (oi) =>
              oi.productSkuId === item.orderItem?.productSkuId ||
              oi.id === item.orderItemId
          );

        const unitPrice = orderItem?.price || orderItem?.unitPrice || 0;
        const quantity = item.quantity || 1; // Use delivered quantity
        const taxableValue = unitPrice * quantity;
        const gstRate = orderItem?.productSku?.product?.gstPercent || 18; // Default 18%

        // For same state transactions (CGST + SGST), for interstate (IGST)
        const isInterstate = false; // You can determine this based on customer state

        let cgstAmount = 0,
          sgstAmount = 0,
          igstAmount = 0;

        if (isInterstate) {
          igstAmount = (taxableValue * gstRate) / 100;
        } else {
          cgstAmount = (taxableValue * gstRate) / 200; // Half rate for CGST
          sgstAmount = (taxableValue * gstRate) / 200; // Half rate for SGST
        }

        const totalAmount = taxableValue + cgstAmount + sgstAmount + igstAmount;

        return {
          srNo: index + 1,
          productSkuId: item.productSkuId || item.orderItem?.productSkuId, // Use productId for delivery items
          productSkuId: item.orderItem?.productSkuId, // Add productSkuId field
          description:
            item.orderItem?.productSku?.name ||
            item.orderItem?.productSku?.description ||
            item.orderItem?.productSku?.product?.description ||
            `Product ${item.productId}`,
          barcode: item.orderItem?.productSku?.barcode || "N/A",
          sku: item.orderItem?.productSku?.sku || "N/A",
          hsnCode: item.orderItem?.productSku?.product?.hsCode || "9999", // Default HSN
          unit: "PCS",
          quantity,
          rate: unitPrice,
          taxableValue,
          gstRate,
          cgstRate: isInterstate ? 0 : gstRate / 2,
          cgstAmount,
          sgstRate: isInterstate ? 0 : gstRate / 2,
          sgstAmount,
          igstRate: isInterstate ? gstRate : 0,
          igstAmount,
          totalAmount,
        };
      });

      // Calculate totals
      const subtotal = items.reduce((sum, item) => sum + item.taxableValue, 0);
      const cgstAmount = items.reduce((sum, item) => sum + item.cgstAmount, 0);
      const sgstAmount = items.reduce((sum, item) => sum + item.sgstAmount, 0);
      const igstAmount = items.reduce((sum, item) => sum + item.igstAmount, 0);
      const totalTax = cgstAmount + sgstAmount + igstAmount;
      const totalBeforeRounding = subtotal + totalTax;
      const totalAmount = Math.round(totalBeforeRounding);
      const roundOff = totalAmount - totalBeforeRounding;

      // Format delivery order numbers
      const doNumbers = deliveryOrders
        .map((do_) => do_.deliveryNumber)
        .join(", ");
      const doDate =
        deliveryOrders.length > 0 && deliveryOrders[0].actualDeliveryDate
          ? format(new Date(deliveryOrders[0].actualDeliveryDate), "yyyy-MM-dd")
          : "";

      setInvoiceData({
        ...invoiceData,
        // invoiceNumber, // Let backend generate this
        poNumber:
          selectedOrder.poNumber ||
          selectedOrder.orderNumber ||
          selectedOrder.id,
        poDate: selectedOrder.createdAt
          ? format(new Date(selectedOrder.createdAt), "yyyy-MM-dd")
          : "",
        deliveryOrderNumber: doNumbers,
        deliveryOrderDate: doDate,
        items,
        subtotal,
        cgstAmount,
        sgstAmount,
        igstAmount,
        roundOff,
        totalAmount,
        amountInWords: convertToWords(totalAmount),
      });
    }
  }, [selectedOrder, isOpen, selectedDeliveryOrders]);

  // Convert number to words for Indian currency
  const convertToWords = (amount) => {
    const units = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
    ];
    const teens = [
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const convertHundreds = (num) => {
      let result = "";
      const hundred = Math.floor(num / 100);
      const remainder = num % 100;

      if (hundred > 0) {
        result += units[hundred] + " Hundred ";
      }

      if (remainder >= 10 && remainder < 20) {
        result += teens[remainder - 10] + " ";
      } else {
        const ten = Math.floor(remainder / 10);
        const unit = remainder % 10;
        if (ten > 0) result += tens[ten] + " ";
        if (unit > 0) result += units[unit] + " ";
      }

      return result;
    };

    if (amount === 0) return "Zero Rupees Only";

    const crores = Math.floor(amount / 10000000);
    const lakhs = Math.floor((amount % 10000000) / 100000);
    const thousands = Math.floor((amount % 100000) / 1000);
    const hundreds = amount % 1000;

    let words = "";

    if (crores > 0) words += convertHundreds(crores) + "Crore ";
    if (lakhs > 0) words += convertHundreds(lakhs) + "Lakh ";
    if (thousands > 0) words += convertHundreds(thousands) + "Thousand ";
    if (hundreds > 0) words += convertHundreds(hundreds);

    return words.trim() + " Rupees Only";
  };

  const handleInputChange = (field, value) => {
    setInvoiceData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...invoiceData.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };

    // Recalculate if quantity or rate changes
    if (field === "quantity" || field === "rate") {
      const item = newItems[index];
      item.taxableValue = item.quantity * item.rate;

      const isInterstate = false; // Determine based on logic
      if (isInterstate) {
        item.igstAmount = (item.taxableValue * item.gstRate) / 100;
        item.cgstAmount = 0;
        item.sgstAmount = 0;
      } else {
        item.cgstAmount = (item.taxableValue * item.gstRate) / 200;
        item.sgstAmount = (item.taxableValue * item.gstRate) / 200;
        item.igstAmount = 0;
      }

      item.totalAmount =
        item.taxableValue + item.cgstAmount + item.sgstAmount + item.igstAmount;
    }

    // Recalculate totals
    const subtotal = newItems.reduce((sum, item) => sum + item.taxableValue, 0);
    const cgstAmount = newItems.reduce((sum, item) => sum + item.cgstAmount, 0);
    const sgstAmount = newItems.reduce((sum, item) => sum + item.sgstAmount, 0);
    const igstAmount = newItems.reduce((sum, item) => sum + item.igstAmount, 0);
    const totalBeforeRounding = subtotal + cgstAmount + sgstAmount + igstAmount;
    const totalAmount = Math.round(totalBeforeRounding);
    const roundOff = totalAmount - totalBeforeRounding;

    setInvoiceData((prev) => ({
      ...prev,
      items: newItems,
      subtotal,
      cgstAmount,
      sgstAmount,
      igstAmount,
      roundOff,
      totalAmount,
      amountInWords: convertToWords(totalAmount),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Include selected delivery order IDs with invoice data
    onConfirmInvoice({
      ...invoiceData,
      deliveryOrderIds: selectedDeliveryOrders,
    });
  };

  const generateInvoicePDF = () => {
    const element = document.getElementById("invoice-pdf-content");
    if (element) {
      // Create optimized wrapper
      const wrapper = document.createElement("div");
      wrapper.className = "page-container";

      // Create optimized header with better layout
      const header = document.createElement("div");
      header.className = "header";
      header.style.cssText = `
        font-family: Arial, sans-serif;
        font-size: 8px;
        color: #333;
        line-height: 1.2;
        margin-bottom: 4mm;
      `;
      header.innerHTML = `
        <!-- Company and Invoice Header -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; width: 100%; margin-bottom: 3mm;">
          <!-- Company Section -->
          <div style="flex: 1; margin-right: 6mm;">
            <div style="margin-bottom: 2mm;">
              <div style="font-size: 9px; font-weight: bold; color: #1f2937; margin-bottom: 1mm; text-transform: uppercase;">YOUR COMPANY</div>
              <div style="display: flex; align-items: center; margin-bottom: 1.5mm;">
                <div style="width: 0.3in; height: 0.3in; background: #2563eb; border-radius: 4px; display: flex; align-items: center; justify-content: center; margin-right: 0.2in;">
                  <span style="color: white; font-size: 12px; font-weight: bold;">E</span>
                </div>
                <div style="color: #2563eb; font-size: 14px; font-weight: bold;">${
                  companyInfo?.name || "Axia B2B Solutions"
                }</div>
              </div>
            </div>
            <div style="font-size: 7px; color: #555; line-height: 1.4;">
              <div style="margin-bottom: 0.5mm;">${
                companyInfo?.address || "123 Business Park, Suite 100"
              }</div>
              <div style="margin-bottom: 0.5mm;">${
                companyInfo?.city || "Tech City"
              }, ${companyInfo?.country || "India"}</div>
              <div style="margin-bottom: 0.5mm;">Phone: ${
                companyInfo?.phone || "+91 98765 43210"
              } | Email: ${companyInfo?.email || "sales@ennovaceb2b.com"}</div>
              <div>GSTIN: ${companyInfo?.gstin || "27ABCDE1234F1Z5"} | PAN: ${
        companyInfo?.pan || "ABCDE1234F"
      }</div>
            </div>
          </div>
          
          <!-- Invoice Details Section -->
          <div style="text-align: right; flex: 1; margin-left: 6mm;">
            <div style="font-size: 16px; font-weight: bold; color: #1f2937; margin-bottom: 2mm; text-transform: uppercase;">Commercial Invoice</div>
            <div style="font-size: 8px; color: #555; line-height: 1.5;">
              <div style="margin-bottom: 0.5mm;"><strong>Invoice No:</strong> ${
                invoiceData.invoiceNumber
              }</div>
              <div style="margin-bottom: 0.5mm;"><strong>Date:</strong> ${
                invoiceData.invoiceDate ||
                new Date().toLocaleDateString("en-GB")
              }</div>
              <div style="margin-bottom: 0.5mm;"><strong>Due Date:</strong> ${
                invoiceData.dueDate || "N/A"
              }</div>
              <div style="margin-bottom: 0.5mm;"><strong>PO Ref:</strong> ${
                selectedOrder?.poNumber || selectedOrder?.orderNumber || "N/A"
              }</div>
              ${
                invoiceData.deliveryOrderNumber
                  ? `<div style="margin-bottom: 0.5mm;"><strong>DO No:</strong> ${invoiceData.deliveryOrderNumber}</div>`
                  : ""
              }
              ${
                invoiceData.deliveryOrderDate
                  ? `<div><strong>DO Date:</strong> ${invoiceData.deliveryOrderDate}</div>`
                  : ""
              }
            </div>
          </div>
        </div>
        
        <!-- Customer Information Section -->
        <div style="margin-top: 4mm; padding: 2.5mm; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 3px;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start;">
            <!-- Bill To -->
            <div style="flex: 1; margin-right: 6mm;">
              <div style="font-size: 9px; font-weight: bold; color: #1f2937; margin-bottom: 1.5mm; text-transform: uppercase;">Bill To</div>
              <div style="font-size: 7px; color: #555; line-height: 1.4;">
                <div style="font-weight: bold; color: #374151; margin-bottom: 0.5mm;">${
                  selectedOrder?.customer?.businessName ||
                  selectedOrder?.customer?.name ||
                  "N/A"
                }</div>
                <div style="margin-bottom: 0.5mm;">${
                  selectedOrder?.customer?.billAddressLine || "N/A"
                }</div>
                <div style="margin-bottom: 0.5mm;">${
                  selectedOrder?.customer?.billDistrict || "N/A"
                }, ${selectedOrder?.customer?.billState || "N/A"} ${
        selectedOrder?.customer?.billPinCode || "N/A"
      }</div>
                <div style="margin-bottom: 0.5mm;">${
                  selectedOrder?.customer?.billCountry || "India"
                }</div>
                <div style="margin-bottom: 0.5mm;"><strong>GSTIN:</strong> ${
                  selectedOrder?.customer?.gstNumber || "N/A"
                }</div>
                <div><strong>Contact:</strong> ${
                  selectedOrder?.customer?.contactMobile || "N/A"
                }</div>
              </div>
            </div>
            
            <!-- Ship To (if different) -->
            ${
              selectedOrder?.customer?.shipAddressLine &&
              selectedOrder?.customer?.shipAddressLine !==
                selectedOrder?.customer?.billAddressLine
                ? `
            <div style="flex: 1; margin-left: 6mm;">
              <div style="font-size: 9px; font-weight: bold; color: #1f2937; margin-bottom: 1.5mm; text-transform: uppercase;">Ship To</div>
              <div style="font-size: 7px; color: #555; line-height: 1.4;">
                <div style="font-weight: bold; color: #374151; margin-bottom: 0.5mm;">${
                  selectedOrder?.customer?.businessName || "N/A"
                }</div>
                <div style="margin-bottom: 0.5mm;">${
                  selectedOrder?.customer?.shipAddressLine || "N/A"
                }</div>
                <div style="margin-bottom: 0.5mm;">${
                  selectedOrder?.customer?.shipDistrict || "N/A"
                }, ${selectedOrder?.customer?.shipState || "N/A"} ${
                    selectedOrder?.customer?.shipPinCode || "N/A"
                  }</div>
                <div>${selectedOrder?.customer?.shipCountry || "India"}</div>
              </div>
            </div>
            `
                : ""
            }
          </div>
        </div>
      `;

      // Add page footer
      const footer = document.createElement("div");
      footer.className = "footer";
      footer.style.cssText = `
        font-size: 7px;
        color: #666;
        display: flex;
        align-items: center;
        justify-content: space-between;
      `;
      footer.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <div>Generated by ${companyInfo?.name || "Axia B2B Solutions"}</div>
          <div style="font-weight: bold; color: #374151;">Page <span class="page-number"></span></div>
          <div>${new Date().toLocaleDateString(
            "en-GB"
          )} ${new Date().toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      })}</div>
        </div>
      `;

      // Add content with proper margins
      const content = document.createElement("div");
      content.className = "content-wrapper";
      content.innerHTML = element.innerHTML;

      // Enhanced CSS with reduced margins and better alignment
      const pageStyle = document.createElement("style");
      pageStyle.textContent = `
        body { 
          margin: 0; 
          padding: 0; 
          font-family: Arial, Helvetica, sans-serif;
          font-size: 9px;
          line-height: 1.2;
        }
        .page-container {
          width: 210mm;
          height: 297mm;
          margin: 0 auto;
          background: white;
          padding: 6mm 2mm 6mm 2mm;
          box-sizing: border-box;
        }
        .header {
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 4mm;
          margin-bottom: 8mm;
        }
        .footer {
          border-top: 1px solid #e5e7eb;
          padding-top: 4mm;
          margin-top: 8mm;
          text-align: center;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
        }
        .content-wrapper {
          min-height: 200mm;
          margin-bottom: 12mm;
        }
        .page-number { 
          font-weight: bold; 
          color: #374151; 
        }
        /* Improved table alignment for totals */
        .totals-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 4mm;
        }
        .totals-table td {
          padding: 2mm;
          border: 1px solid #d1d5db;
          text-align: right;
          font-weight: bold;
        }
        .totals-table td:first-child {
          text-align: left;
          width: 60%;
        }
        .totals-table td:not(:first-child) {
          width: 13.33%;
        }
        /* Better spacing for company info */
        .company-info {
          margin-bottom: 6mm;
        }
        .company-info h1 {
          margin: 0 0 2mm 0;
          font-size: 14px;
          font-weight: bold;
          color: #1f2937;
        }
        .company-info p {
          margin: 1mm 0;
          font-size: 8px;
          color: #6b7280;
        }
        /* Enhanced totals section alignment */
        .totals-section {
          background-color: #f9fafb !important;
          border-top: 2px solid #d1d5db !important;
        }
        .totals-section td {
          font-weight: bold !important;
          font-size: 9px !important;
          padding: 2mm 1.5mm !important;
          color: #374151 !important;
        }
        .totals-section td:first-child {
          text-align: right !important;
          font-size: 10px !important;
          font-weight: bold !important;
        }
        .totals-section td:last-child {
          background-color: #e5e7eb !important;
          font-size: 10px !important;
          font-weight: bold !important;
          color: #1f2937 !important;
        }
        /* Totals row styling */
        .totals-section tr {
          border-top: 2px solid #d1d5db !important;
        }
        /* Enhanced table improvements for PDF */
        table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-bottom: 3mm !important;
          font-size: 7px !important;
        }
        table td, table th {
          border: 1px solid #d1d5db !important;
          padding: 1.5mm !important;
          font-size: 7px !important;
          vertical-align: middle !important;
          line-height: 1.2 !important;
        }
        table th {
          background-color: #f3f4f6 !important;
          font-weight: bold !important;
          text-align: center !important;
          font-size: 8px !important;
          color: #374151 !important;
        }
        table td {
          color: #555 !important;
        }
        /* Better column alignment */
        table td:nth-child(1) { text-align: center !important; } /* Sr. No */
        table td:nth-child(2) { text-align: left !important; } /* Description */
        table td:nth-child(3) { text-align: center !important; } /* SKU */
        table td:nth-child(4) { text-align: center !important; } /* Barcode */
        table td:nth-child(5) { text-align: center !important; } /* HSN */
        table td:nth-child(6) { text-align: center !important; } /* UOM */
        table td:nth-child(7) { text-align: right !important; } /* Qty */
        table td:nth-child(8) { text-align: right !important; } /* Rate */
        table td:nth-child(9) { text-align: right !important; } /* Taxable Value */
        table td:nth-child(10) { text-align: center !important; } /* CGST */
        table td:nth-child(11) { text-align: center !important; } /* SGST */
        table td:nth-child(12) { text-align: center !important; } /* IGST */
        table td:nth-child(13) { text-align: right !important; } /* Total */
        /* Customer information section styling */
        .customer-info-section {
          background-color: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 2px !important;
          padding: 3mm !important;
          margin-top: 6mm !important;
        }
        .customer-info-section h4 {
          font-size: 9px !important;
          font-weight: bold !important;
          color: #1f2937 !important;
          margin-bottom: 2mm !important;
        }
        .customer-info-section p {
          font-size: 7px !important;
          color: #666 !important;
          line-height: 1.3 !important;
          margin: 1mm 0 !important;
        }
      `;
      wrapper.appendChild(pageStyle);

      wrapper.appendChild(header);
      wrapper.appendChild(footer);
      wrapper.appendChild(content);

      const opt = {
        margin: [2, 2, 2, 2],
        filename: `${invoiceData.invoiceNumber}.pdf`,
        image: {
          type: "jpeg",
          quality: 0.98,
        },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          width: 210,
          height: 297,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
      };

      html2pdf().set(opt).from(wrapper).save();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-2 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl my-4 flex flex-col max-h-[95vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 rounded-t-lg flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">SALES INVOICE</h2>
              <p className="text-blue-100 text-sm">
                Professional Business Invoice
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs text-blue-100">Invoice Number</div>
                <div className="text-lg font-mono font-bold">
                  {invoiceData.invoiceNumber}
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-blue-100 hover:text-white transition-colors p-1"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div id="invoice-pdf-content" className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Company Header */}
              <div className="border-b-2 border-gray-300 pb-4">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span className="text-blue-600 font-bold text-lg">
                        LOGO
                      </span>
                    </div>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">
                        {companyInfo?.name || "Axia B2B Solutions"}
                      </h1>
                      <p className="text-gray-600">
                        {companyInfo?.address || "123 Business Park, Suite 100"}
                      </p>
                      <p className="text-gray-600">
                        {companyInfo?.city || "Tech City"},{" "}
                        {companyInfo?.country || "India"}
                      </p>
                      <p className="text-gray-600">
                        GSTIN: {companyInfo?.gstin || "27ABCDE1234F1Z5"} | PAN:{" "}
                        {companyInfo?.pan || "ABCDE1234F"}
                      </p>
                      <p className="text-gray-600">
                        Email: {companyInfo?.email || "sales@ennovaceb2b.com"} |
                        Phone: {companyInfo?.phone || "+91 98765 43210"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <h2 className="text-xl font-bold text-gray-900">
                      SALES INVOICE
                    </h2>
                    <p className="text-gray-600">Original for Recipient</p>
                  </div>
                </div>
              </div>

              {/* Delivery Order Selection */}
              {selectedOrder?.deliveryOrders &&
                selectedOrder.deliveryOrders.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h3 className="font-semibold text-gray-800 mb-3">
                      Select Delivery Orders for Invoice
                    </h3>
                    <div className="space-y-2">
                      {selectedOrder.deliveryOrders
                        .filter((do_) => do_.deliveryStatus === "delivered")
                        .map((deliveryOrder) => {
                          const isInvoiced = deliveryOrder.invoiced;
                          const isSelected = selectedDeliveryOrders.includes(
                            deliveryOrder.id
                          );

                          return (
                            <label
                              key={deliveryOrder.id}
                              className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
                                isInvoiced
                                  ? "bg-gray-100 border-gray-300 opacity-60 cursor-not-allowed"
                                  : isSelected
                                  ? "bg-blue-100 border-blue-500"
                                  : "bg-white border-gray-300 hover:border-blue-300"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isInvoiced}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedDeliveryOrders([
                                      ...selectedDeliveryOrders,
                                      deliveryOrder.id,
                                    ]);
                                  } else {
                                    setSelectedDeliveryOrders(
                                      selectedDeliveryOrders.filter(
                                        (id) => id !== deliveryOrder.id
                                      )
                                    );
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">
                                    {deliveryOrder.deliveryNumber}
                                  </span>
                                  <span
                                    className={`px-2 py-0.5 text-xs rounded-full ${
                                      isInvoiced
                                        ? "bg-gray-200 text-gray-600"
                                        : "bg-green-100 text-green-700"
                                    }`}
                                  >
                                    {isInvoiced
                                      ? "Already Invoiced"
                                      : "Available"}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-600">
                                  {deliveryOrder.actualDeliveryDate &&
                                    format(
                                      new Date(
                                        deliveryOrder.actualDeliveryDate
                                      ),
                                      "dd MMM yyyy"
                                    )}
                                  {" • "}
                                  {deliveryOrder.items?.length || 0} items
                                </div>
                              </div>
                            </label>
                          );
                        })}
                    </div>
                    {selectedDeliveryOrders.length === 0 && (
                      <p className="text-sm text-orange-600 mt-2">
                        Please select at least one delivery order to create an
                        invoice.
                      </p>
                    )}
                  </div>
                )}

              {/* Invoice Details and Customer Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Invoice Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-3">
                    Invoice Details
                  </h3>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Invoice Number
                        </label>
                        <input
                          type="text"
                          value={invoiceData.invoiceNumber}
                          onChange={(e) =>
                            handleInputChange("invoiceNumber", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100"
                          placeholder="Auto-generated"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Invoice Date
                        </label>
                        <input
                          type="date"
                          value={invoiceData.invoiceDate}
                          onChange={(e) =>
                            handleInputChange("invoiceDate", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          PO Number
                        </label>
                        <input
                          type="text"
                          value={invoiceData.poNumber}
                          onChange={(e) =>
                            handleInputChange("poNumber", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          PO Date
                        </label>
                        <input
                          type="date"
                          value={invoiceData.poDate}
                          onChange={(e) =>
                            handleInputChange("poDate", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Delivery Order Number
                        </label>
                        <input
                          type="text"
                          value={invoiceData.deliveryOrderNumber}
                          onChange={(e) =>
                            handleInputChange(
                              "deliveryOrderNumber",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="DO-XXXX-XXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Delivery Order Date
                        </label>
                        <input
                          type="date"
                          value={invoiceData.deliveryOrderDate}
                          onChange={(e) =>
                            handleInputChange(
                              "deliveryOrderDate",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Due Date
                        </label>
                        <input
                          type="date"
                          value={invoiceData.dueDate}
                          onChange={(e) =>
                            handleInputChange("dueDate", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Payment Terms
                        </label>
                        <select
                          value={invoiceData.paymentTerms}
                          onChange={(e) =>
                            handleInputChange("paymentTerms", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="Net 30 Days">Net 30 Days</option>
                          <option value="Net 15 Days">Net 15 Days</option>
                          <option value="Net 7 Days">Net 7 Days</option>
                          <option value="Due on Receipt">Due on Receipt</option>
                          <option value="Cash on Delivery">
                            Cash on Delivery
                          </option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Place of Supply
                      </label>
                      <input
                        type="text"
                        value={invoiceData.placeOfSupply}
                        onChange={(e) =>
                          handleInputChange("placeOfSupply", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        placeholder="State/UT"
                      />
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-3">Bill To</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <strong>
                        {selectedOrder?.customer?.businessName ||
                          selectedOrder?.customer?.name}
                      </strong>
                    </div>
                    <div>{selectedOrder?.customer?.billAddressLine}</div>
                    <div>
                      {selectedOrder?.customer?.billDistrict},{" "}
                      {selectedOrder?.customer?.billState}{" "}
                      {selectedOrder?.customer?.billPinCode}
                    </div>
                    <div>{selectedOrder?.customer?.billCountry}</div>
                    <div>
                      <strong>GSTIN:</strong>{" "}
                      {selectedOrder?.customer?.gstNumber || "N/A"}
                    </div>
                    <div>
                      <strong>PAN:</strong>{" "}
                      {selectedOrder?.customer?.panNumber || "N/A"}
                    </div>
                    <div>
                      <strong>Contact:</strong>{" "}
                      {selectedOrder?.customer?.contactMobile}
                    </div>
                    <div>
                      <strong>Email:</strong> {selectedOrder?.customer?.email}
                    </div>
                  </div>

                  {/* Ship To (if different) */}
                  {selectedOrder?.customer?.shipAddressLine &&
                    selectedOrder?.customer?.shipAddressLine !==
                      selectedOrder?.customer?.billAddressLine && (
                      <div className="mt-4 pt-3 border-t border-blue-200">
                        <h4 className="font-medium text-gray-800 mb-2">
                          Ship To
                        </h4>
                        <div className="space-y-1 text-sm">
                          <div>{selectedOrder?.customer?.businessName}</div>
                          <div>{selectedOrder?.customer?.shipAddressLine}</div>
                          <div>
                            {selectedOrder?.customer?.shipDistrict},{" "}
                            {selectedOrder?.customer?.shipState}{" "}
                            {selectedOrder?.customer?.shipPinCode}
                          </div>
                          <div>{selectedOrder?.customer?.shipCountry}</div>
                        </div>
                      </div>
                    )}
                </div>
              </div>

              {/* Items Table */}
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-4 py-2">
                  <h3 className="font-semibold text-gray-800">Invoice Items</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-50">
                      <tr className="text-xs">
                        <th className="border border-gray-300 px-2 py-2 text-left">
                          Sr.
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-left">
                          Description of Goods
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-left">
                          SKU
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-left">
                          Barcode
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-left">
                          HSN/SAC
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-left">
                          UOM
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-right">
                          Qty
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-right">
                          Rate
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-right">
                          Taxable Value
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-center">
                          CGST
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-center">
                          SGST
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-center">
                          IGST
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-right">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData.items.map((item, index) => (
                        <tr key={index} className="text-xs">
                          <td className="border border-gray-300 px-2 py-2 text-center">
                            {item.srNo}
                          </td>
                          <td className="border border-gray-300 px-2 py-2">
                            {item.description}
                          </td>
                          <td className="border border-gray-300 px-2 py-2 text-center">
                            {item.sku}
                          </td>
                          <td className="border border-gray-300 px-2 py-2 text-center">
                            {item.barcode}
                          </td>
                          <td className="border border-gray-300 px-2 py-2 text-center">
                            {item.hsnCode}
                          </td>
                          <td className="border border-gray-300 px-2 py-2 text-center">
                            {item.unit}
                          </td>
                          <td className="border border-gray-300 px-2 py-2 text-right">
                            {item.quantity}
                          </td>
                          <td className="border border-gray-300 px-2 py-2 text-right">
                            ₹{item.rate.toFixed(2)}
                          </td>
                          <td className="border border-gray-300 px-2 py-2 text-right">
                            ₹{item.taxableValue.toFixed(2)}
                          </td>
                          <td className="border border-gray-300 px-1 py-2 text-center">
                            {item.cgstRate > 0 && (
                              <div>
                                <div>{item.cgstRate}%</div>
                                <div>₹{item.cgstAmount.toFixed(2)}</div>
                              </div>
                            )}
                          </td>
                          <td className="border border-gray-300 px-1 py-2 text-center">
                            {item.sgstRate > 0 && (
                              <div>
                                <div>{item.sgstRate}%</div>
                                <div>₹{item.sgstAmount.toFixed(2)}</div>
                              </div>
                            )}
                          </td>
                          <td className="border border-gray-300 px-1 py-2 text-center">
                            {item.igstRate > 0 && (
                              <div>
                                <div>{item.igstRate}%</div>
                                <div>₹{item.igstAmount.toFixed(2)}</div>
                              </div>
                            )}
                          </td>
                          <td className="border border-gray-300 px-2 py-2 text-right font-medium">
                            ₹{item.totalAmount.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 font-medium totals-section">
                      <tr className="text-sm">
                        <td
                          colSpan="8"
                          className="border border-gray-300 px-2 py-2 text-right font-bold"
                        >
                          TOTAL
                        </td>
                        <td className="border border-gray-300 px-2 py-2 text-right font-bold">
                          ₹{invoiceData.subtotal.toFixed(2)}
                        </td>
                        <td className="border border-gray-300 px-2 py-2 text-center font-bold">
                          ₹{invoiceData.cgstAmount.toFixed(2)}
                        </td>
                        <td className="border border-gray-300 px-2 py-2 text-center font-bold">
                          ₹{invoiceData.sgstAmount.toFixed(2)}
                        </td>
                        <td className="border border-gray-300 px-2 py-2 text-center font-bold">
                          ₹{invoiceData.igstAmount.toFixed(2)}
                        </td>
                        <td className="border border-gray-300 px-2 py-2 text-right font-bold">
                          ₹{invoiceData.totalAmount.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Amount Summary and Bank Details */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Amount in Words */}
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-2">
                    Amount in Words
                  </h3>
                  <p className="text-sm font-medium text-gray-900">
                    {invoiceData.amountInWords}
                  </p>

                  {invoiceData.roundOff !== 0 && (
                    <div className="mt-3 pt-2 border-t border-yellow-200">
                      <div className="text-sm">
                        <div className="flex justify-between">
                          <span>Round Off:</span>
                          <span>₹{invoiceData.roundOff.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bank Details */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-2">
                    Bank Details
                  </h3>
                  <div className="text-sm space-y-1">
                    <div>
                      <strong>Bank Name:</strong>{" "}
                      {companyInfo?.bankDetails?.bankName || "HDFC Bank"}
                    </div>
                    <div>
                      <strong>Account Name:</strong>{" "}
                      {companyInfo?.bankDetails?.accountName ||
                        "Axia B2B Solutions"}
                    </div>
                    <div>
                      <strong>Account Number:</strong>{" "}
                      {companyInfo?.bankDetails?.accountNumber ||
                        "1234567890123456"}
                    </div>
                    <div>
                      <strong>IFSC Code:</strong>{" "}
                      {companyInfo?.bankDetails?.ifscCode || "HDFC0001234"}
                    </div>
                    <div>
                      <strong>Branch:</strong>{" "}
                      {companyInfo?.bankDetails?.branch || "Tech City Branch"}
                    </div>
                    <div>
                      <strong>Account Type:</strong>{" "}
                      {companyInfo?.bankDetails?.accountType ||
                        "Current Account"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">
                  Terms and Conditions
                </h3>
                <textarea
                  value={invoiceData.termsAndConditions}
                  onChange={(e) =>
                    handleInputChange("termsAndConditions", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  rows="4"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={invoiceData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  rows="2"
                  placeholder="Any additional notes or instructions..."
                />
              </div>

              {/* Authorization */}
              <div className="text-right border-t pt-4">
                <div className="inline-block text-center">
                  <div className="w-48 h-20 border-b border-gray-300 mb-2"></div>
                  <p className="text-sm font-medium">Authorized Signatory</p>
                  <p className="text-xs text-gray-600">
                    {companyInfo?.name || "Axia B2B Solutions"}
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-100 px-6 py-4 border-t flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <strong>Total Amount:</strong> ₹
            {invoiceData.totalAmount.toLocaleString()}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={generateInvoicePDF}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
            >
              Download PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={creatingInvoice || selectedDeliveryOrders.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {creatingInvoice ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating...
                </>
              ) : (
                "Create Commercial Invoice"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommercialInvoiceForm;
